/**
 * The sound-effect vocabulary (R26).
 *
 * Every event the game fires has a recipe here, and `synth.test.ts` enumerates
 * the vocabulary and fails on any gap — a weapon with no sound is a weapon that
 * feels broken, and it is exactly the sort of omission nobody notices until a
 * play session.
 */

import { Synth, ENV_PERCUSSIVE, ENV_SOFT, type Envelope } from './synth.js'

/** Every sound the game can make. The test asserts each has a recipe. */
export const SFX = {
  WeaponSmallArms: 'weapon:small-arms',
  WeaponCannon: 'weapon:cannon',
  WeaponArc: 'weapon:arc',
  WeaponMortar: 'weapon:mortar',
  Impact: 'impact',
  ExplosionSmall: 'explosion:small',
  ExplosionLarge: 'explosion:large',
  StructureCollapse: 'structure:collapse',
  Select: 'ui:select',
  OrderAck: 'ui:order',
  Reject: 'ui:reject',
  Click: 'ui:click',
  BuildStart: 'build:start',
  BuildComplete: 'build:complete',
  Place: 'build:place',
  HarvestUnload: 'econ:unload',
  PowerLow: 'econ:power-low',
  SuperReady: 'super:ready',
  SuperFire: 'super:fire',
} as const

export type SfxName = (typeof SFX)[keyof typeof SFX]

export const ALL_SFX: SfxName[] = Object.values(SFX)

const ENV_TAIL: Envelope = { attack: 0.002, decay: 0.35, sustain: 0, release: 0.3 }
const ENV_BOOM: Envelope = { attack: 0.004, decay: 0.6, sustain: 0, release: 0.5 }

/**
 * Play a named effect into `destination`. Returns the context time at which the
 * voice is silent, so the mixer can reclaim its slot.
 */
export function playSfx(
  synth: Synth,
  name: SfxName,
  destination: AudioNode,
  start: number,
  pitch = 1,
): number {
  const t = start
  switch (name) {
    // --- weapons: three layers each, which is what gives them weight --------
    case SFX.WeaponSmallArms: {
      const a = synth.noiseBurst({
        peak: 0.5,
        env: ENV_PERCUSSIVE,
        filter: 'bandpass',
        frequency: 2400 * pitch,
        q: 1.6,
        start: t,
        destination,
      })
      const b = synth.tone({
        type: 'square',
        from: 320 * pitch,
        to: 120 * pitch,
        peak: 0.18,
        env: ENV_PERCUSSIVE,
        start: t,
        destination,
      })
      return Math.max(a, b)
    }
    case SFX.WeaponCannon: {
      const thump = synth.tone({
        type: 'sine',
        from: 140 * pitch,
        to: 42 * pitch,
        peak: 0.65,
        env: ENV_TAIL,
        start: t,
        destination,
      })
      const crack = synth.tone({
        type: 'sawtooth',
        from: 620 * pitch,
        to: 180 * pitch,
        peak: 0.3,
        env: ENV_PERCUSSIVE,
        start: t,
        destination,
      })
      const tail = synth.noiseBurst({
        peak: 0.35,
        env: ENV_TAIL,
        filter: 'lowpass',
        frequency: 1800,
        start: t + 0.005,
        destination,
      })
      return Math.max(thump, crack, tail)
    }
    case SFX.WeaponArc: {
      const zap = synth.tone({
        type: 'sawtooth',
        from: 1800 * pitch,
        to: 300 * pitch,
        peak: 0.3,
        env: { attack: 0.002, decay: 0.12, sustain: 0, release: 0.08 },
        start: t,
        destination,
      })
      const fizz = synth.noiseBurst({
        peak: 0.25,
        env: ENV_PERCUSSIVE,
        filter: 'highpass',
        frequency: 3000,
        start: t,
        destination,
      })
      return Math.max(zap, fizz)
    }
    case SFX.WeaponMortar: {
      const cough = synth.tone({
        type: 'sine',
        from: 90 * pitch,
        to: 55 * pitch,
        peak: 0.55,
        env: ENV_TAIL,
        start: t,
        destination,
      })
      const air = synth.noiseBurst({
        peak: 0.28,
        env: { attack: 0.01, decay: 0.25, sustain: 0, release: 0.2 },
        filter: 'bandpass',
        frequency: 700,
        start: t,
        destination,
      })
      return Math.max(cough, air)
    }

    // --- impacts ------------------------------------------------------------
    case SFX.Impact:
      return synth.noiseBurst({
        peak: 0.4,
        env: ENV_PERCUSSIVE,
        filter: 'bandpass',
        frequency: 900 * pitch,
        q: 0.8,
        start: t,
        destination,
      })
    case SFX.ExplosionSmall: {
      const boom = synth.tone({
        type: 'sine',
        from: 180,
        to: 40,
        peak: 0.6,
        env: ENV_TAIL,
        start: t,
        destination,
      })
      const debris = synth.noiseBurst({
        peak: 0.45,
        env: ENV_TAIL,
        filter: 'lowpass',
        frequency: 1400,
        start: t,
        destination,
      })
      return Math.max(boom, debris)
    }
    case SFX.ExplosionLarge: {
      const boom = synth.tone({
        type: 'sine',
        from: 120,
        to: 28,
        peak: 0.85,
        env: ENV_BOOM,
        start: t,
        destination,
      })
      const roar = synth.noiseBurst({
        peak: 0.6,
        env: ENV_BOOM,
        filter: 'lowpass',
        frequency: 900,
        start: t,
        destination,
      })
      const crack = synth.noiseBurst({
        peak: 0.3,
        env: ENV_PERCUSSIVE,
        filter: 'highpass',
        frequency: 2500,
        start: t,
        destination,
      })
      return Math.max(boom, roar, crack)
    }
    case SFX.StructureCollapse: {
      const groan = synth.tone({
        type: 'triangle',
        from: 220,
        to: 45,
        peak: 0.5,
        env: ENV_BOOM,
        hold: 0.15,
        start: t,
        destination,
      })
      const rubble = synth.noiseBurst({
        peak: 0.5,
        env: { attack: 0.05, decay: 0.9, sustain: 0, release: 0.4 },
        filter: 'lowpass',
        frequency: 700,
        start: t + 0.05,
        destination,
      })
      return Math.max(groan, rubble)
    }

    // --- interface ----------------------------------------------------------
    case SFX.Select:
      return synth.tone({
        type: 'square',
        from: 880,
        to: 1320,
        peak: 0.12,
        env: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.03 },
        start: t,
        destination,
      })
    case SFX.OrderAck:
      return synth.tone({
        type: 'square',
        from: 660,
        to: 990,
        peak: 0.1,
        env: { attack: 0.001, decay: 0.04, sustain: 0, release: 0.03 },
        start: t,
        destination,
      })
    case SFX.Reject:
      // Descending, and deliberately sour. A rejection has to be audible as a
      // rejection before the alert line is read (R33).
      return synth.tone({
        type: 'square',
        from: 300,
        to: 140,
        peak: 0.18,
        env: { attack: 0.001, decay: 0.14, sustain: 0, release: 0.08 },
        start: t,
        destination,
      })
    case SFX.Click:
      return synth.noiseBurst({
        peak: 0.09,
        env: { attack: 0.001, decay: 0.02, sustain: 0, release: 0.01 },
        filter: 'highpass',
        frequency: 4000,
        start: t,
        destination,
      })

    // --- production and economy ---------------------------------------------
    case SFX.BuildStart:
      return synth.tone({
        type: 'triangle',
        from: 220,
        to: 330,
        peak: 0.16,
        env: ENV_SOFT,
        hold: 0.05,
        start: t,
        destination,
      })
    case SFX.BuildComplete: {
      const a = synth.tone({
        type: 'triangle',
        from: 440,
        peak: 0.16,
        env: ENV_SOFT,
        start: t,
        destination,
      })
      const b = synth.tone({
        type: 'triangle',
        from: 660,
        peak: 0.14,
        env: ENV_SOFT,
        start: t + 0.09,
        destination,
      })
      return Math.max(a, b)
    }
    case SFX.Place: {
      const thud = synth.tone({
        type: 'sine',
        from: 160,
        to: 60,
        peak: 0.45,
        env: ENV_TAIL,
        start: t,
        destination,
      })
      const dust = synth.noiseBurst({
        peak: 0.3,
        env: ENV_TAIL,
        filter: 'lowpass',
        frequency: 900,
        start: t,
        destination,
      })
      return Math.max(thud, dust)
    }
    case SFX.HarvestUnload:
      return synth.noiseBurst({
        peak: 0.22,
        env: { attack: 0.02, decay: 0.4, sustain: 0.2, release: 0.25 },
        filter: 'bandpass',
        frequency: 600,
        q: 0.5,
        start: t,
        destination,
      })
    case SFX.PowerLow:
      return synth.tone({
        type: 'sawtooth',
        from: 180,
        to: 120,
        peak: 0.2,
        env: { attack: 0.01, decay: 0.3, sustain: 0.3, release: 0.3 },
        hold: 0.2,
        start: t,
        destination,
      })

    // --- superweapon --------------------------------------------------------
    case SFX.SuperReady: {
      const a = synth.tone({
        type: 'sine',
        from: 330,
        to: 660,
        peak: 0.2,
        env: ENV_SOFT,
        hold: 0.2,
        start: t,
        destination,
      })
      const b = synth.tone({
        type: 'sine',
        from: 495,
        to: 990,
        peak: 0.14,
        env: ENV_SOFT,
        hold: 0.2,
        start: t + 0.05,
        destination,
      })
      return Math.max(a, b)
    }
    case SFX.SuperFire: {
      const charge = synth.tone({
        type: 'sawtooth',
        from: 80,
        to: 1400,
        peak: 0.4,
        env: { attack: 0.3, decay: 0.4, sustain: 0.4, release: 0.6 },
        hold: 0.3,
        start: t,
        destination,
      })
      const blast = synth.noiseBurst({
        peak: 0.7,
        env: ENV_BOOM,
        filter: 'lowpass',
        frequency: 1100,
        start: t + 0.8,
        destination,
      })
      return Math.max(charge, blast)
    }
    default:
      return t
  }
}
